import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getProductReviews, voteReviewHelpful } from "@/lib/reviews.functions";
import { StarRating } from "./StarRating";
import { ReviewDisclosure } from "./ReviewDisclosure";
import { ReviewPhotoLightbox, type LightboxPhoto } from "./ReviewPhotoLightbox";
import { VerticalReviewSlider } from "./VerticalReviewSlider";
import { ThumbsUp } from "lucide-react";
import { VerifiedPurchaseBadge } from "./VerifiedPurchaseBadge";
import { BetaTesterBadge } from "./BetaTesterBadge";

type Sort = "newest" | "highest" | "lowest" | "helpful";

const VISITOR_KEY_STORAGE = "truwe_review_voter";
const VOTED_STORAGE = "truwe_review_voted";

function getVisitorKey(): string {
  if (typeof window === "undefined") return "";
  try {
    let key = window.localStorage.getItem(VISITOR_KEY_STORAGE);
    if (!key) {
      key = crypto.randomUUID();
      window.localStorage.setItem(VISITOR_KEY_STORAGE, key);
    }
    return key;
  } catch {
    return "";
  }
}

function readVoted(): string[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(window.localStorage.getItem(VOTED_STORAGE) || "[]");
  } catch {
    return [];
  }
}

export function ReviewsSection({ productId }: { productId: string }) {
  const [sort, setSort] = useState<Sort>("newest");
  const [filter, setFilter] = useState<number | null>(null);
  const [voted, setVoted] = useState<string[]>(() => readVoted());
  const [bump, setBump] = useState<Record<string, number>>({});
  const [lightbox, setLightbox] = useState<number | null>(null);
  const fn = useServerFn(getProductReviews);
  const voteFn = useServerFn(voteReviewHelpful);

  const { data, isLoading } = useQuery({
    queryKey: ["reviews", productId, sort, filter],
    queryFn: () => fn({ data: { productId, sort, rating: filter ?? undefined } }),
  });

  const reviews = data?.reviews ?? [];
  const stats = data?.stats ?? { avg: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number> };
  const total = stats.count || 1;

  const photos: LightboxPhoto[] = useMemo(() => {
    const out: LightboxPhoto[] = [];
    reviews.forEach((r: any) => {
      (Array.isArray(r.images) ? r.images : []).forEach((url: string) => {
        out.push({
          url,
          rating: r.rating,
          name: r.reviewer_name || "Verified buyer",
          comment: r.comment,
          title: r.title,
        });
      });
    });
    return out;
  }, [reviews]);

  function photoIndex(url: string) {
    return Math.max(0, photos.findIndex((p) => p.url === url));
  }

  async function markHelpful(reviewId: string) {
    if (voted.includes(reviewId)) return;
    const next = [...voted, reviewId];
    setVoted(next);
    setBump((b) => ({ ...b, [reviewId]: (b[reviewId] ?? 0) + 1 }));
    try {
      window.localStorage.setItem(VOTED_STORAGE, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
    try {
      await voteFn({ data: { reviewId, visitorKey: getVisitorKey() } });
    } catch {
      /* vote is best-effort */
    }
  }

  return (
    <section id="reviews" className="scroll-mt-24 border-t border-[#D9D9D6] bg-white px-5 py-12 md:px-10 md:py-16">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8 flex items-end justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#1E5EFF]">Customer reviews</p>
            <h2 className="mt-1 font-display text-2xl font-semibold text-[#2C2C2C] md:text-3xl">
              What real users are saying
            </h2>
          </div>
        </header>

        {photos.length > 0 && (
          <div className="mb-8">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#2C2C2C]/50">
              Photos from customers
            </p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {photos.map((p, i) => (
                <button
                  key={`${p.url}-${i}`}
                  onClick={() => setLightbox(i)}
                  className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-[#D9D9D6] md:h-28 md:w-28"
                  aria-label={`Open photo from a ${p.rating}-star review by ${p.name}`}
                >
                  <img
                    src={p.url}
                    alt={`Customer photo from a ${p.rating}-star review by ${p.name}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-8 rounded-2xl border border-[#D9D9D6] bg-[#F7F7F5] p-6 md:grid-cols-[260px,1fr] md:p-8">
          <div className="flex flex-col items-start gap-2">
            <div className="text-4xl font-semibold tabular-nums text-[#2C2C2C]">
              {stats.avg.toFixed(1)}
            </div>
            <StarRating value={stats.avg} size={18} />
            <p className="text-sm text-[#2C2C2C]/60">Based on {stats.count} {stats.count === 1 ? "review" : "reviews"}</p>
          </div>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((r) => {
              const count = stats.distribution[r] || 0;
              const pct = stats.count ? (count / total) * 100 : 0;
              const active = filter === r;
              return (
                <button
                  key={r}
                  onClick={() => setFilter(active ? null : r)}
                  className={`flex w-full items-center gap-3 rounded px-2 py-1 text-left text-sm transition ${
                    active ? "bg-[#1E5EFF]/10" : "hover:bg-white"
                  }`}
                >
                  <span className="w-6 tabular-nums">{r}★</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-white">
                    <span className="block h-full bg-amber-400" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="w-8 text-right tabular-nums text-[#2C2C2C]/60">{count}</span>
                </button>
              );
            })}
            {filter !== null && (
              <button onClick={() => setFilter(null)} className="text-xs text-[#1E5EFF] hover:underline">
                Clear filter
              </button>
            )}
          </div>
        </div>

        <div className="mt-6">
          <ReviewDisclosure />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#2C2C2C]/60">{stats.count} {stats.count === 1 ? "review" : "reviews"}</p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#2C2C2C]/60">Sort:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              className="rounded border border-[#D9D9D6] bg-white px-2 py-1 text-sm"
            >
              <option value="newest">Newest</option>
              <option value="helpful">Most helpful</option>
              <option value="highest">Highest rated</option>
              <option value="lowest">Lowest rated</option>
            </select>
          </div>
        </div>

        <div className="mt-5">
          {isLoading && <p className="text-sm text-[#2C2C2C]/60">Loading reviews…</p>}
          {!isLoading && reviews.length === 0 && (
            <p className="rounded-xl border border-dashed border-[#D9D9D6] bg-[#F7F7F5] p-6 text-center text-sm text-[#2C2C2C]/60">
              No reviews yet. Be the first to share your experience after your protocol arrives.
            </p>
          )}
          {!isLoading && reviews.length > 0 && (
            <VerticalReviewSlider
              height={232}
              slides={reviews.map((r: any) => {
                const helpful = (r.helpful_count ?? 0) + (bump[r.id] ?? 0);
                const hasVoted = voted.includes(r.id);
                return {
                  key: r.id,
                  node: (
                    <article className="rounded-xl border border-[#D9D9D6] bg-white p-3.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <StarRating value={r.rating} size={14} />
                        <span className="text-sm font-medium text-[#2C2C2C]">
                          {r.reviewer_name ||
                            (r.reviewer_kind === "beta_tester" ? "Beta tester" : "Verified buyer")}
                        </span>
                        {r.reviewer_kind === "beta_tester" ? (
                          <BetaTesterBadge />
                        ) : (
                          r.is_verified && <VerifiedPurchaseBadge orderRef={r.order_ref} />
                        )}
                        <span className="ml-auto text-[11px] text-[#2C2C2C]/50">
                          {new Date(r.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                        </span>
                      </div>
                      {r.title && (
                        <h3 className="mt-1.5 text-sm font-medium text-[#2C2C2C]">{r.title}</h3>
                      )}
                      {r.comment && (
                        <p className="mt-1.5 whitespace-pre-line text-[13.5px] leading-snug text-[#2C2C2C]/80">
                          {r.comment}
                        </p>
                      )}
                      {Array.isArray(r.images) && r.images.length > 0 && (
                        <div className="mt-2 flex gap-2">
                          {r.images.map((url: string, i: number) => (
                            <button
                              key={i}
                              onClick={() => setLightbox(photoIndex(url))}
                              className="block h-14 w-14 overflow-hidden rounded-lg border border-[#D9D9D6]"
                              aria-label="Open review photo"
                            >
                              <img
                                src={url}
                                alt="Photo shared by a customer with their review"
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                      {r.admin_reply && (
                        <div className="mt-2.5 rounded-lg border border-[#1E5EFF]/20 bg-[#1E5EFF]/5 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1E5EFF]">
                            Response from TruWe
                            {r.admin_reply_at && (
                              <span className="ml-2 font-normal normal-case tracking-normal text-[#2C2C2C]/50">
                                {new Date(r.admin_reply_at).toLocaleDateString("en-IN", {
                                  dateStyle: "medium",
                                })}
                              </span>
                            )}
                          </p>
                          <p className="mt-1 whitespace-pre-line text-[13px] leading-snug text-[#2C2C2C]/80">
                            {r.admin_reply}
                          </p>
                        </div>
                      )}
                      <div className="mt-2.5 flex items-center gap-2">
                        <button
                          onClick={() => markHelpful(r.id)}
                          disabled={hasVoted}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                            hasVoted
                              ? "border-[#1E5EFF]/30 bg-[#1E5EFF]/10 text-[#1E5EFF]"
                              : "border-[#D9D9D6] text-[#2C2C2C]/70 hover:bg-[#F7F7F5]"
                          }`}
                        >
                          <ThumbsUp size={12} />
                          {hasVoted ? "Marked helpful" : "Helpful"}
                        </button>
                        {helpful > 0 && (
                          <span className="text-[11px] text-[#2C2C2C]/50">
                            {helpful} {helpful === 1 ? "person" : "people"} found this helpful
                          </span>
                        )}
                      </div>
                    </article>
                  ),
                };
              })}
            />
          )}
        </div>
      </div>

      {lightbox !== null && photos[lightbox] && (
        <ReviewPhotoLightbox
          photos={photos}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}
